/**
 * Ocean Booking Parties Fields Component
 *
 * Renders the parties section of the Ocean Booking form.
 * Parties can be: SHIPPER, CONSIGNEE, BOOKING_AGENT, NOTIFY_PARTY, CARRIER.
 *
 * Uses react-hook-form's useFieldArray for dynamic array management.
 * Users can add, edit, and remove parties in edit mode.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { COUNTRY_OPTIONS } from '../../constants/countries';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * Severity color map for inline issue indicators on select fields
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * Party type options for ocean bookings
 */
const PARTY_TYPES = [
  { value: 'SHIPPER', label: 'Shipper' },
  { value: 'CONSIGNEE', label: 'Consignee' },
  { value: 'BOOKING_AGENT', label: 'Booking Agent' },
  { value: 'NOTIFY_PARTY', label: 'Notify Party' },
  { value: 'CARRIER', label: 'Carrier' },
  { value: 'BENEFICIAL_CARGO_OWNER', label: 'Beneficial Cargo Owner' }
];

/**
 * OBPartiesFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function OBPartiesFields({ control, register, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parties'
  });

  /**
   * Add a new empty party
   */
  const addParty = () => {
    append({
      type: 'SHIPPER',
      name: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      contactName: '',
      phone: '',
      email: '',
      identifyingCodes: []
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Parties
        </h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addParty}>
            + Add Party
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No parties added yet. Click "Add Party" to add shipper, consignee, or other party information.
        </div>
      )}

      {/* Party entries */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="border border-gray-200 rounded-lg p-4 space-y-3"
        >
          {/* Party header with remove button */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Party #{index + 1}
            </span>
            {isEditMode && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>

          {/* Party fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Party Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Type
                  <IssueIndicator issues={getFieldIssues(issues, `parties[${index}].type`)} />
                </span>
              </label>
              <select
                {...register(`parties.${index}.type`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {PARTY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Name - full width */}
            <div className="md:col-span-2">
              <Input
                label="Name"
                {...register(`parties.${index}.name`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].name`)}
                placeholder="Company or person name"
              />
            </div>

            {/* Address fields */}
            <div className="md:col-span-2">
              <Input
                label="Address Line 1"
                {...register(`parties.${index}.address.addressLine1`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine1`)}
                placeholder="Street address"
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label="Address Line 2"
                {...register(`parties.${index}.address.addressLine2`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine2`)}
                placeholder="Suite, building, floor (optional)"
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label="Address Line 3"
                {...register(`parties.${index}.address.addressLine3`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine3`)}
                placeholder="Additional address info (optional)"
              />
            </div>

            <Input
              label="City"
              {...register(`parties.${index}.address.city`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `parties[${index}].address.city`)}
            />

            <Input
              label="State/Province"
              {...register(`parties.${index}.address.state`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `parties[${index}].address.state`)}
            />

            <Input
              label="Postal Code"
              {...register(`parties.${index}.address.postalCode`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `parties[${index}].address.postalCode`)}
            />

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Country
                  <IssueIndicator issues={getFieldIssues(issues, `parties[${index}].address.country`)} />
                </span>
              </label>
              <select
                {...register(`parties.${index}.address.country`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {COUNTRY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="border-t pt-3 mt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Contact Name"
                {...register(`parties.${index}.contactName`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].contactName`)}
              />

              <Input
                label="Phone"
                {...register(`parties.${index}.phone`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].phone`)}
              />

              <Input
                label="Email"
                {...register(`parties.${index}.email`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].email`)}
              />
            </div>
          </div>

          {/* Identifying Codes (read-only if present) */}
          {field.identifyingCodes && field.identifyingCodes.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Identifying Codes</h4>
              <div className="space-y-1">
                {field.identifyingCodes.map((code, codeIdx) => (
                  <div key={codeIdx} className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 rounded px-3 py-1.5">
                    <span className="font-medium">{code.codeListProvider || code.type || 'Code'}:</span>
                    <span>{code.partyCode || code.value || ''}</span>
                    {code.codeListName && (
                      <span className="text-gray-500">({code.codeListName})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default OBPartiesFields;
